import React, { PropsWithChildren, ReactNode, useEffect, useState } from 'react'
import { Collapse, Container, Stack } from 'react-bootstrap'
import classNames from 'classnames'

export type DefaultCollapsedState = 'collapsed' | 'expanded'

export type CollapsablePanelProps = {
  id: string
  header: ReactNode
  defaultCollapsedState: DefaultCollapsedState
  shouldStoreCollapsedState?: boolean
  headerWhileCollapsed?: ReactNode
  hideBorderWhileExpanded?: boolean
} & PropsWithChildren

const getLocalStorageKey = (id: string) => {
  return `is_collapsed_${id}`
}

const getInitialCollapsedState = (id: string, state: DefaultCollapsedState, shouldStoreCollapsedState?: boolean) => {
  if (shouldStoreCollapsedState) {
    const isCollapsed = window.localStorage ? window.localStorage.getItem(getLocalStorageKey(id)) : null
    if (isCollapsed) {
      return JSON.parse(isCollapsed)
    }
  }
  if (state === 'collapsed') {
    return true
  }
  if (state === 'expanded') {
    return false
  }
  return false
}

export const CollapsiblePanel: React.FC<CollapsablePanelProps> = ({
  id,
  defaultCollapsedState,
  shouldStoreCollapsedState,
  header,
  headerWhileCollapsed,
  hideBorderWhileExpanded,
  children,
}) => {
  const [isCollapsed, setCollapsed] = useState(true)

  useEffect(() => {
    setCollapsed(getInitialCollapsedState(id, defaultCollapsedState, shouldStoreCollapsedState))
  }, [id, defaultCollapsedState, shouldStoreCollapsedState])

  const handleCollapseToggle = () => {
    setCollapsed((isCollapsed) => {
      if (window.localStorage) {
        window.localStorage.setItem(getLocalStorageKey(id), `${!isCollapsed}`)
      }
      return !isCollapsed
    })
  }

  return (
    <div className={`d-flex flex-column gap-3 h-100 ${!isCollapsed && hideBorderWhileExpanded ? '' : 'border zocks-border-0.5'} p-3`}>
      <Stack direction='horizontal' gap={2} className='d-flex align-items-center gap-3' onClick={handleCollapseToggle}>
        {isCollapsed ? (headerWhileCollapsed ?? header) : header}
        <i className={classNames('bi ms-auto', { 'bi-chevron-expand': isCollapsed, 'bi-chevron-contract': !isCollapsed })} />
      </Stack>
      {/* do not remove conditional-rendering below! bootstrap has a bug in Collapse component.
        in case it gets rendered too early, it screws up svg icons on the page  */}
      {!isCollapsed ? (
        <Collapse in={!isCollapsed} dimension='height'>
          <Container>{children}</Container>
        </Collapse>
      ) : null}
    </div>
  )
}
